import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import investorPortalApi from "@/lib/investorPortalApi";

const formatDate = (iso, lang = "de") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "de-DE", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const EVENT_ACCENT = {
  investor_submitted: "bg-skyMist",
  investor_status_changed: "bg-butterCream",
  investor_magic_link_used: "bg-ivory",
  investor_profile_updated: "bg-ivory",
  investor_thesis_updated: "bg-sageMint",
  investor_2fa_enabled: "bg-lavenderFog",
  investor_2fa_disabled: "bg-roseMilk",
  investor_referral_created: "bg-butterCream",
};

export default function InvestorTimeline() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    investorPortalApi.get("/timeline")
      .then(({ data }) => {
        setEvents(data.events || []);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6" data-testid="investor-portal-timeline">
      <motion.header initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}>
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.timeline.eyebrow")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]">{t("invPortal.timeline.title")}</h1>
        <p className="mt-3 text-warmGrey">{t("invPortal.timeline.lead")}</p>
      </motion.header>

      {loading ? (
        <div className="rounded-3xl border border-sandBeige bg-white p-8 flex items-center justify-center text-warmGrey gap-2">
          <Loader2 className="animate-spin" size={18} /> {t("invPortal.timeline.loading")}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-3xl border border-sandBeige bg-white p-10 text-center" data-testid="inv-timeline-empty">
          <p className="font-serif text-2xl text-cocoa">{t("invPortal.timeline.emptyTitle")}</p>
          <p className="text-warmGrey text-sm mt-2">{t("invPortal.timeline.emptyBody")}</p>
        </div>
      ) : (
        <ol className="relative flex flex-col gap-4" data-testid="inv-timeline-list">
          <span aria-hidden className="absolute left-3 sm:left-4 top-2 bottom-2 w-px bg-sandBeige" />
          {events.map((ev, i) => {
            const accent = EVENT_ACCENT[ev.event_type] || "bg-ivory";
            return (
              <motion.li key={ev.id || i}
                initial={{ opacity: 0, x: -6 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.3, ease: "easeOut", delay: Math.min(i, 6) * 0.04 }}
                className="relative pl-10 sm:pl-14"
                data-testid={`inv-timeline-${i}`}
              >
                <span className={`absolute left-1.5 sm:left-2.5 top-3 w-4 h-4 rounded-full border-2 border-white ${accent}`} aria-hidden />
                <div className="rounded-2xl border border-sandBeige bg-white p-4 sm:p-5 shadow-soft">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="font-medium text-cocoa text-sm">
                      {t(`invPortal.events.${ev.event_type}`, { defaultValue: ev.event_type })}
                    </p>
                    <p className="text-xs text-warmGrey">{formatDate(ev.timestamp, lang)}</p>
                  </div>
                </div>
              </motion.li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
